'use client';

import type posthog from 'posthog-js';

/**
 * Returns the PostHog client if it has been initialized on the window,
 * or null if not available. Avoids hard-coupling to posthog-js at
 * import time so callers never throw when PostHog is absent.
 */
function getPostHog(): typeof posthog | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const ph = (window as unknown as Record<string, unknown>).posthog as
    | typeof posthog
    | undefined;

  if (!ph || typeof ph.capture !== 'function') {
    return null;
  }

  return ph;
}

// ---------------------------------------------------------------------------
// Landing page events
// ---------------------------------------------------------------------------

/**
 * Tracks a ChatSuite landing page view with vertical and location context.
 */
export function trackChatSuitePageView(props: {
  vertical: string;
  city: string;
  country: string;
}): void {
  const ph = getPostHog();

  if (!ph) return;

  ph.capture('chatsuite_page_view', props);
}

/**
 * Tracks when a user views the pricing section, optionally with a tier.
 */
export function trackChatSuitePricingView(props: { tier?: string }): void {
  const ph = getPostHog();

  if (!ph) return;

  ph.capture('chatsuite_pricing_view', props);
}

/**
 * Tracks a demo CTA click with the source location on the page.
 */
export function trackChatSuiteDemoClick(props: { source: string }): void {
  const ph = getPostHog();

  if (!ph) return;

  ph.capture('chatsuite_demo_click', props);
}

/**
 * Tracks when the comparison section comes into view.
 */
export function trackChatSuiteComparisonView(): void {
  const ph = getPostHog();

  if (!ph) return;

  ph.capture('chatsuite_comparison_view', {});
}

/**
 * Tracks interest in a specific ChatSuite feature.
 */
export function trackChatSuiteFeatureInterest(props: {
  feature: string;
}): void {
  const ph = getPostHog();

  if (!ph) return;

  ph.capture('chatsuite_feature_interest', props);
}

// ---------------------------------------------------------------------------
// Conversion events
// ---------------------------------------------------------------------------

/**
 * Tracks the start of a ChatSuite application form.
 */
export function trackChatSuiteApplyStart(props: {
  vertical: string;
  city: string;
}): void {
  const ph = getPostHog();

  if (!ph) return;

  ph.capture('chatsuite_apply_start', props);
}

/**
 * Tracks successful completion of a ChatSuite application form.
 */
export function trackChatSuiteApplyComplete(props: {
  vertical: string;
  city: string;
  businessType: string;
  employeeCount: string;
}): void {
  const ph = getPostHog();

  if (!ph) return;

  ph.capture('chatsuite_apply_complete', props);
}
